import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldArray, formValueSelector, reduxForm } from 'redux-form/immutable';
import { WrappedFieldArrayProps } from 'redux-form';
import { Map, List } from 'immutable';
import moment, { Moment } from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Button,
  FormControl,
} from '@material-ui/core';
import { Divider, Grid, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { Disclaimer, DisclaimerContentType } from 'entities/organization/model';
import { selectAccessibleUserLocations } from 'entities/user/selectors';
import Box from 'retired/Box';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormSelect from 'retired/FormSelect';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import FormFileUpload from 'retired/FormFileUpload';
import FormTextField from 'retired/FormTextField';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormDateSelector from 'retired/FormDateSelector';
import FormCheckbox from 'retired/FormCheckbox';
import { imageTypes } from 'retired/FileUpload/utils';
import DialogButton from 'retired/DialogButton';
import * as validators from 'utils/validators';

import { DISCLAIMERS_FORM_IDENTIFIER } from './constants';

const notEmpty = validators.notEmpty();

const ContentTypeField = (props: any) => (
  <FormSelect {...props}>
    {['IMAGE', 'TEXT'].map((contentType) => (
      <MenuItem key={contentType} value={contentType}>
        {contentType}
      </MenuItem>
    ))}
  </FormSelect>
);

const emptyDisclaimer: Disclaimer = {
  _id: '',
  contentType: 'TEXT',
  content: '',
  disclaimerExpire: 0,
  buttonText: '',
  locations: [],
  locationsExclude: true,
};

const validateLocations = (
  value: List<string>,
  entity: Map<string, Disclaimer>
): string | undefined => {
  if (entity && !entity.get('locationsExclude') && !value?.size) {
    return 'You selected to only include certain locations and none are selected';
  }
  return undefined;
};

const validateDate = (
  value: Moment,
  disclaimer: Map<string, Disclaimer>,
  fieldName: 'startingAt' | 'endingAt'
) => {
  // Dates are optional
  if (!value) {
    return undefined;
  }

  if (!moment.isMoment(value)) {
    // eslint-disable-next-line no-param-reassign
    value = moment(value);
  }

  if (
    fieldName === 'startingAt' &&
    disclaimer.get('endingAt') &&
    value.isAfter(moment(disclaimer.get('endingAt') as unknown as string | Moment))
  ) {
    return "Start date can't be after end date.";
  }

  if (
    fieldName === 'endingAt' &&
    disclaimer.get('startingAt') &&
    value.isBefore(moment(disclaimer.get('startingAt') as unknown as string | Moment))
  ) {
    return "End date can't be before start date.";
  }

  return undefined;
};

const DisclaimersForm = (
  props: WrappedFieldArrayProps<Map<string, Disclaimer>> & {
    locations: ReturnType<typeof selectAccessibleUserLocations>;
    collapsedAccordion: Record<string, boolean>;
    setCollapsedAccordion: (disclaimers: Record<string, boolean>) => void;
  }
) => {
  const { collapsedAccordion, setCollapsedAccordion, fields, locations } = props;
  return (
    <div style={{ marginBottom: 30 }}>
      <Button onClick={(): void => fields.push(Map(emptyDisclaimer))}>
        <AddIcon /> Add new disclaimer
      </Button>
      <Divider style={{ marginTop: 10, marginBottom: 15 }} />

      {fields.length
        ? fields.map((disclaimer, index) => {
            const currentDisclaimer = props.fields.get(index) as unknown as Map<string, Disclaimer>;
            const isImageDisclaimer =
              (currentDisclaimer.get('contentType') as unknown as DisclaimerContentType) ===
              'IMAGE';

            return (
              <Accordion
                key={disclaimer}
                expanded={collapsedAccordion[index]}
                onChange={() => {
                  collapsedAccordion[index] = !collapsedAccordion[index];
                  setCollapsedAccordion(collapsedAccordion);
                }}
              >
                <AccordionSummary>Disclaimer {currentDisclaimer.get('_id')}</AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={4} marginBottom={1}>
                    <Grid item xs={12}>
                      <FormControl style={{ width: '100%', height: 17 }}>
                        <FormFieldLabel forName={`${disclaimer}.contentType`}>
                          Disclaimer type
                        </FormFieldLabel>
                      </FormControl>
                      <FormControl style={{ width: '100%' }}>
                        <Field name={`${disclaimer}.contentType`} component={ContentTypeField} />
                      </FormControl>

                      <Divider />

                      <div style={{ marginTop: 15 }}>
                        <FormControl style={{ width: '100%', height: 17 }}>
                          <FormFieldLabel forName={`${disclaimer}.content`}>Content</FormFieldLabel>
                        </FormControl>
                        {isImageDisclaimer ? (
                          <Field
                            id="disclaimerImage"
                            name={`${disclaimer}.content`}
                            component={FormFileUpload}
                            labelText="Upload an image in PNG or JPG format."
                            acceptedTypes={imageTypes}
                            imagePreview
                            validate={[notEmpty]}
                          />
                        ) : (
                          <FormControl style={{ width: '100%' }}>
                            <Field
                              component={FormDescriptionRteField}
                              name={`${disclaimer}.content`}
                              fullWidth
                              multiline
                              validate={[notEmpty]}
                            />
                          </FormControl>
                        )}
                      </div>
                    </Grid>

                    <Grid item xs={12} xl={6}>
                      <FormControl style={{ width: '100%' }}>
                        <FormFieldLabel forName={`${disclaimer}.buttonText`}>
                          Agreement button text
                          <div>
                            <small>
                              By default the button will say &quot;I agree&quot; translated on all
                              supported languages. The custom text will be used regardless of the
                              employee preferred language.
                            </small>
                          </div>
                        </FormFieldLabel>
                      </FormControl>
                      <FormControl style={{ width: '100%' }}>
                        <Field
                          name={`${disclaimer}.buttonText`}
                          component={FormTextField}
                          inputProps={{ maxLength: 100, autoComplete: 'off' }}
                          placeholder="I agree"
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      <FormControl style={{ width: '100%' }}>
                        <FormFieldLabel forName={`${disclaimer}.disclaimerExpire`}>
                          Expiration
                          <div>
                            <small>
                              How long (in minutes) before the disclaimer expires and needs to be
                              agreed again. Set it to zero for no expiration.
                            </small>
                          </div>
                        </FormFieldLabel>
                      </FormControl>
                      <FormControl style={{ width: '100%' }}>
                        <Field
                          name={`${disclaimer}.disclaimerExpire`}
                          component={FormTextField}
                          type="number"
                          placeholder="In minutes"
                        />
                      </FormControl>
                    </Grid>

                    <Grid item>
                      <FormControl style={{ width: '100%', height: 17 }}>
                        <FormFieldLabel forName={`${disclaimer}.startingAt`}>
                          Start date
                        </FormFieldLabel>
                      </FormControl>
                      <Field
                        name={`${disclaimer}.startingAt`}
                        component={FormDateSelector}
                        validate={(value: Moment) =>
                          validateDate(value, currentDisclaimer, 'startingAt')
                        }
                        noTime
                      />
                    </Grid>
                    <Grid item>
                      <FormControl style={{ width: '100%', height: 17 }}>
                        <FormFieldLabel forName={`${disclaimer}.endingAt`}>End date</FormFieldLabel>
                      </FormControl>
                      <Field
                        name={`${disclaimer}.endingAt`}
                        component={FormDateSelector}
                        validate={(value: Moment) =>
                          validateDate(value, currentDisclaimer, 'endingAt')
                        }
                        noTime
                      />
                    </Grid>

                    <Grid item xs={12} xl={4}>
                      <FormIncludeExcludeSelector
                        name={`${disclaimer}.locationsExclude`}
                        labelText="the following locations:"
                      />
                      <Field
                        name={`${disclaimer}.locations`}
                        component={FormLocationSelector}
                        props={{ locations }}
                        validate={(value: List<string>) =>
                          validateLocations(value, currentDisclaimer)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Field
                        name={`${disclaimer}.cleanUserConsent`}
                        component={FormCheckbox}
                        label="Check this box to reset the consent of all employees to this disclaimer."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider style={{ marginBottom: 15 }} />
                      <DialogButton
                        style={{ display: 'inline-block' }}
                        title="Remove Disclaimer"
                        text="Are you sure you want to remove this Disclaimer? Don't forget to submit the form to save your changes."
                        confirmLabel="Remove"
                        cancelLabel="Cancel"
                        iconButton
                        handleConfirm={(): void => fields.remove(index)}
                      >
                        <DeleteIcon />
                      </DialogButton>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        : null}
    </div>
  );
};

const valueSelector = formValueSelector(DISCLAIMERS_FORM_IDENTIFIER);

const DisclaimersSection = (props: any) => {
  const { handleSubmit, submitForm } = props;
  const disclaimers = useSelector((state) => valueSelector(state, 'disclaimers'));
  const locations = useSelector(selectAccessibleUserLocations);

  const [collapsedAccordion, setCollapsedAccordion] = useState(
    (disclaimers?.toArray() ?? []).map(() => false)
  );

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Box style={{ paddingTop: 0, marginBottom: 30 }}>
        <h2>Disclaimers</h2>
        <p>Presented to the employee when they first login. Only one disclaimer is displayed.</p>

        <FieldArray
          name="disclaimers"
          component={DisclaimersForm}
          locations={locations}
          collapsedAccordion={collapsedAccordion}
          setCollapsedAccordion={setCollapsedAccordion}
        />

        <Typography align="right">
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </Typography>
      </Box>
    </form>
  );
};

const withForm = reduxForm({
  form: DISCLAIMERS_FORM_IDENTIFIER,
  enableReinitialize: true,
  destroyOnUnmount: false,
  keepDirtyOnReinitialize: true,
});

export default withForm(DisclaimersSection);
